import React from 'react';
import { IonContent, IonPage, useIonRouter } from '@ionic/react';
import { RouteComponentProps } from 'react-router';
import { Button, Alert } from 'react-bootstrap';
import { axius, func, images, types } from 'utils';
import { Header, IonLoading, TabsOffset } from 'components';
import { Form, Input } from 'antd';
import { _store } from '_Store';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Filesystem } from '@capacitor/filesystem';

const jQuery = Object(window).jQuery;

const supportTypes = [
	{
		code: 'Order Issue',
		label: 'Help with an order',
		subs: [
			{ code: 'Not Delivered', label: 'My order was not delivered' },
			{ code: 'Can not Track Order', label: 'I cannot track my order' },
			{ code: 'Wrong Order', label: 'I received the wrong order' },
			{
				code: 'Missing Item(s)',
				label: 'One or more items were missing from my order',
			},
			{ code: 'Late Delivery', label: 'My order arrived late' },
			{
				code: 'Food Quality',
				label: 'There was a problem with the food quality',
			},
			{
				code: 'Order Cancelled Unexpectedly',
				label: 'My order was cancelled without explanation',
			},
		],
	},
	{
		code: 'Payment/Refund',
		label: 'Help with a payment or refund',
		subs: [
			{
				code: 'Refund Status',
				label: 'I want to check the status of my refund',
			},
			{ code: 'Charged Incorrectly', label: 'I was charged the wrong amount' },
			{
				code: 'Payment Failed but Deducted',
				label: 'My payment failed but money was still deducted',
			},
			{ code: 'Promo Code not Applied', label: 'My promo code did not apply' },
		],
	},
	{
		code: 'App or Account Issue',
		label: 'Help with the app or my account',
		subs: [
			{ code: 'App Crashing', label: 'The app keeps crashing or freezing' },
			{
				code: 'Login / Account Access',
				label: 'I am having trouble logging in or accessing my account',
			},
			{ code: 'Checkout Stuck', label: 'The app got stuck during checkout' },
		],
	},
	{
		code: 'Rider/Delivery Experience',
		label: 'Feedback about my rider or delivery',
		subs: [
			{ code: 'Rider Conduct', label: "I want to report my rider's conduct" },
			{
				code: 'Rider Could not Find Address',
				label: 'My rider could not find my address',
			},
			{
				code: 'Rider Requested Extra Payment',
				label: 'My rider asked for extra payment',
			},
			{
				code: 'Other Delivery Complaint',
				label: 'I have another complaint about my delivery',
			},
		],
	},
	{
		code: 'Other/General Enquiry',
		label: 'Something else or a general question',
		subs: [
			{ code: 'Product Enquiry', label: 'I have a question about a product' },
			{ code: 'Feedback', label: 'I want to share feedback' },
			{ code: 'Something Else', label: 'My issue is not listed here' },
		],
	},
];

const HelpListItem: React.FC<{ label: string; onClick: () => void }> = ({
	label,
	onClick,
}) => (
	<div
		className="d-flex justify-content-between align-items-center mb-3 p-3 pointer"
		style={{
			border: '1px solid #ebeff6',
			borderRadius: 10,
			backgroundColor: '#fff',
		}}
		onClick={onClick}
	>
		<div className="fs-14">{label}</div>
		<i className="fa fa-angle-right text-muted" />
	</div>
);

interface Props extends RouteComponentProps, types.StateProps {}
const Help: React.FC<Props> = (props) => {
	const {
		__data: { order },
		__auth: { user, isLoggedIn },
	} = _store.useState();

	const [form] = Form.useForm();
	const router = useIonRouter();

	const ord = order;
	const isLow = props.location.pathname.split('/')[3];
	const isOrder = props.location.pathname.split('/')[2];
	const orderIssueType =
		supportTypes.find((t) => t.code === 'Order Issue') || null;

	const [step, setStep] = React.useState(isLow ? 3 : isOrder ? 2 : 1);
	const [selectedType, setSelectedType] = React.useState<
		(typeof supportTypes)[number] | null
	>(isOrder ? orderIssueType : null);
	const [selectedSub, setSelectedSub] = React.useState<{
		code: string;
		label: string;
	} | null>(null);
	const [data, setData] = React.useState<any>({});
	const [files, setFiles] = React.useState<File[]>([]);
	const [imagis, setImages] = React.useState<string[]>([]);
	const [errMsg, setErrMsg] = React.useState('');
	const [loading, setLoading] = React.useState('');

	const readImage = (e: any) => {
		let fale = e.target.files[0];
		if (fale) {
			let reader = new FileReader();
			reader.onload = (r) => {
				setFiles((sf) => sf.concat(fale));
				setImages((si) => si.concat(reader.result as any));
			};
			reader.readAsDataURL(fale);
		}
	};

	const removeImage = (i: number) => {
		setFiles((sf) => sf.filter((o: any, ii: number) => i !== ii));
		setImages((si) => si.filter((o: any, ii: number) => i !== ii));
	};

	const validate = {
		email: (_: any, value: string) => {
			const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!regex.test(value)) {
				return Promise.reject(`Email must be valid`);
			}
			return Promise.resolve();
		},
	};

	const submit = (v: any) => {
		setErrMsg('');
		setLoading('submitting...');
		if (isLow) {
			v['type'] = 'Incident';
		} else if (selectedType && selectedSub) {
			v['type'] = `${selectedType.code} - ${selectedSub.code}`;
		}
		if (isOrder) {
			v['orderId'] = isOrder;
		}
		if (!user.id && v['name']) {
			v['firstName'] = v['name'].split(' ')[0];
			v['lastName'] = v['name'].split(' ')[1] || '';
		}
		if (user.id) {
			v['email'] = user.email;
			v['lastName'] = user.lastName;
			v['firstName'] = user.firstName;
			v['phoneNumber'] = user.phoneNumber;
		}
		axius
			.postFile(`tickets`, { ...v, files })
			.then((res: types.AxiusResponse) => {
				if (res.status === 201) {
					setStep(4);
					setData({ ...v, ...res });
				} else {
					setErrMsg(res.message);
				}
				setLoading('');
			});
	};

	const handleFilePicker = async () => {
		try {
			const result = await FilePicker.pickImages({ limit: 1 });

			if (result.files.length > 0) {
				const file = result.files[0];
				console.log({ file });

				if (!file.path || !file.name) return;

				// Read file using Capacitor Filesystem to handle content:// URIs
				const fileData = await Filesystem.readFile({
					path: file.path,
				});

				// Convert base64 to Blob
				const byteCharacters = atob(fileData.data as string);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: file.mimeType });
				const fakeFile = new File([blob], file.name, { type: file.mimeType });
				console.log({ fakeFile });

				const reader = new FileReader();
				reader.onload = (r) => {
					console.log({ fakeFile }, reader.result);
					setFiles((sf) => sf.concat(fakeFile));
					setImages((si) => si.concat(reader.result as any));
				};
				reader.readAsDataURL(fakeFile);
			}
		} catch (error) {
			console.error('File picker error:', error);
		}
	};

	return (
		<IonPage id="Help">
			<Header
				{...props}
				showSearch={false}
				showBack={step === 2 || step === 3}
				goBack={() => {
					if (step === 2) {
						isOrder ? props.history.goBack() : setStep(1);
					} else if (step === 3) {
						isLow ? props.history.goBack() : setStep(2);
					}
				}}
			/>

			<IonContent fullscreen={false}>
				<IonLoading
					open={loading ? true : false}
					message={loading}
					onCancel={() => setLoading('')}
				/>

				{step === 1 && (
					<div className="p-3">
						<h4 className="fw-600 mb-0">How Can We Help?</h4>
						<div>&nbsp;</div>

						{supportTypes.map((t) => (
							<HelpListItem
								key={t.code}
								label={t.label}
								onClick={() => {
									setSelectedType(t);
									setSelectedSub(null);
									setStep(2);
								}}
							/>
						))}
					</div>
				)}

				{step === 2 && (
					<div className="p-3">
						{isOrder && (
							<h4 className="fw-600 mb-3 primary">Order #{ord.id}</h4>
						)}
						<h4 className="fw-600 mb-0">{selectedType?.label}</h4>
						<div>&nbsp;</div>

						{selectedType?.subs.map((s) => (
							<HelpListItem
								key={s.code}
								label={s.label}
								onClick={() => {
									setSelectedSub(s);
									setStep(3);
								}}
							/>
						))}
					</div>
				)}

				{step === 3 && (
					<div className="p-3">
						{isOrder && !isLow && (
							<h4 className="fw-600 mb-3 primary">Order #{ord.id}</h4>
						)}
						{isLow ? (
							<h4 className="fw-600 mb-0">
								Oh No!, We were hoping for a better rating.
							</h4>
						) : (
							<h4 className="fw-600 mb-0">{selectedType?.label}</h4>
						)}
						<div>&nbsp;</div>

						{errMsg && (
							<Alert variant="danger" className="text-center">
								{errMsg}
							</Alert>
						)}

						<Form
							form={form}
							layout="vertical"
							onFinish={submit}
							className="mt-4"
						>
							{!isLoggedIn && (
								<>
									<Form.Item
										label="Name"
										name="name"
										rules={[func.requiredInput]}
									>
										<Input size="large" className="primary" />
									</Form.Item>
									<Form.Item
										label="Email"
										name="email"
										initialValue={user.email}
										rules={[func.requiredInput, { validator: validate.email }]}
									>
										<Input size="large" className="primary" />
									</Form.Item>
								</>
							)}
							<Form.Item
								label={
									isOrder ? 'How could we have made it better?' : 'Comment'
								}
								name="description"
								rules={[func.requiredInput]}
							>
								<Input.TextArea size="large" rows={4} className="primary" />
							</Form.Item>

							<label>
								Upload files, if any. (You can upload up to 3 files)
							</label>
							<div className="row">
								{imagis.map((image, i) => (
									<div key={image} className="col-4">
										<div
											id="Upload"
											className="bg-img rounded"
											style={{ backgroundImage: `url(${image})` }}
										>
											<div className="rem" onClick={() => removeImage(i)}>
												x
											</div>
										</div>
									</div>
								))}
								{imagis.length < 3 && (
									<div className="col-4">
										<div
											id="Upload"
											className="text-center d-flex align-items-center justify-content-center"
											onClick={() => {
												if (func.isMobile) {
													handleFilePicker();
												} else {
													jQuery('#upload_file').click();
												}
											}}
										>
											<div>
												<input
													type="file"
													accept={'image/*'}
													id="upload_file"
													className="hide"
													onChange={readImage}
												/>
												<img src={images.Upload} alt="Upload" height={20} />
												<div className="small mt-2">Click here to upload</div>
											</div>
										</div>
									</div>
								)}
							</div>
							<p>&nbsp;</p>

							<Button
								type="submit"
								variant="secondary btn-block btn-md"
								style={{ height: 49 }}
							>
								Submit
							</Button>
						</Form>
					</div>
				)}

				{step === 4 && (
					<div
						className="d-flex justify-content-center align-items-center"
						style={{ height: '67vh' }}
					>
						<div style={{ width: '90vw' }}>
							<div
								className="d-flex justify-content-center align-items-center mx-auto"
								style={{
									width: 68,
									height: 68,
									backgroundColor: '#E8F5E9',
									borderRadius: '100%',
								}}
							>
								<img src={images.Smiley} alt="Smiley" />
							</div>
							<div>&nbsp;</div>
							<div className="text-center">
								<b className="fw-600 mb-0">Request received</b>
							</div>
							<div className="small text-muted text-center">
								Ticket #{data?.reference}
							</div>
							<div>&nbsp;</div>
							<div
								className="small mb-4"
								style={{
									padding: 16,
									borderRadius: 10,
									backgroundColor: '#FFF8E1',
									border: '1px solid #FFE082',
									color: '#795548',
								}}
							>
								<div className="bold">We have received your ticket</div>
								<div className="bold mb-1" style={{ color: '#000' }}>
									{func.sanitize.email(data.email)}
								</div>
								<div>
									Our support team will respond there within{' '}
									<b>10-20 minutes</b>.
								</div>
								<div>
									Please check your inbox — and your spam folder if you don't
									see it.
								</div>
							</div>
							<div
								className="mb-4"
								style={{
									padding: 16,
									borderRadius: 10,
									backgroundColor: '#F9FAFB',
									border: '1px solid #eee',
									color: '#6B7280',
									fontSize: 12,
								}}
							>
								<div className="text-uppercase bold">
									Please don't raise another ticket
								</div>
								<div>
									Your request is already with our team. Submitting again won't
									speed things up — it creates a duplicate and can slow down
									your resolution.
								</div>
							</div>
							<Button
								type="button"
								variant="secondary btn-block btn-md"
								style={{ height: 49 }}
								onClick={() => {
									setStep(1);
									router.push('/');
								}}
							>
								Back to Home
							</Button>
						</div>
					</div>
				)}

				<div
					className="text-center muted small mt-4"
					onClick={() => func.clearCache()}
				>
					Republic Dash v{func.app.version}
				</div>
				<TabsOffset />
			</IonContent>
		</IonPage>
	);
};

export default Help;
